import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Grid,
  Tooltip,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  CircularProgress,
  keyframes
} from '@mui/material';
import {
  Visibility,
  Download,
  Warning,
  Error,
  Info,
  PlayArrow,
  Pause,
  Fullscreen,
  ExpandMore,
  Screenshot,
  Videocam,
  FormatQuote,
  PhotoCamera
} from '@mui/icons-material';
import { Infraction, infractionsAPI } from '../../services/apiService';
import { CachedEvidence } from '../common/CachedImage';

interface InfractionProps {
  interviewId: string;
  infractions?: Infraction[];
  onJumpToReplay?: (timestamp: number) => void;
  isRoutineMonitoring?: boolean;
  seekingTimestamp?: number | null;
}

// Premium micro-animations for cinematic UI experience
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const slideInStaggered = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(20px) scale(0.95); 
  }
  100% { 
    opacity: 1; 
    transform: translateY(0px) scale(1); 
  }
`;

const InfractionComponent: React.FC<InfractionProps> = ({
  interviewId,
  infractions = [],
  onJumpToReplay,
  isRoutineMonitoring = false,
  seekingTimestamp = null
}) => {
  const [selectedInfraction, setSelectedInfraction] = useState<Infraction | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInfractions, setLoadingInfractions] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [allInfractions, setAllInfractions] = useState<Infraction[]>(infractions);
  const [inlinePreviewUrls, setInlinePreviewUrls] = useState<Record<string, string>>({});

  // Load infractions from API if not provided
  useEffect(() => {
    const loadInfractions = async () => {
      if (infractions.length === 0 && interviewId) {
        setLoadingInfractions(true);
        try {
          const loadedInfractions = await infractionsAPI.getInfractions(interviewId);
          // Sort infractions chronologically (earliest first) in frontend for reliability
          const sortedInfractions = loadedInfractions.sort((a, b) => {
            // Extract timestamp from multiple possible sources for reliable sorting
            const getTimestamp = (infraction: any) => {
              // Try timestamp field first
              if (infraction.timestamp) {
                const ts = new Date(infraction.timestamp).getTime();
                if (!isNaN(ts)) return ts;
              }
              
              // Try date_created field
              if (infraction.date_created) {
                const ts = new Date(infraction.date_created).getTime();
                if (!isNaN(ts)) return ts;
              }
              
              // Extract from SK field as fallback (format: viol_1762058680051_xxx or 1762058704-screenshot_upload)
              if (infraction.sk) {
                const match = infraction.sk.match(/(\d{13})/); // Look for 13-digit timestamp
                if (match) {
                  return parseInt(match[1], 10);
                }
              }
              
              // Fallback to 0 if no timestamp found
              console.warn('[INFRACTION_SORT] No valid timestamp found for infraction:', infraction.sk);
              return 0;
            };
            
            const timestampA = getTimestamp(a);
            const timestampB = getTimestamp(b);
            
            // Debug logging to verify sorting
            if (Math.abs(timestampA - timestampB) > 1000) { // Only log if timestamps differ by more than 1 second
              console.log('[INFRACTION_SORT] Comparing:', {
                aId: a.sk,
                aTime: new Date(timestampA).toISOString(),
                bId: b.sk, 
                bTime: new Date(timestampB).toISOString(),
                result: timestampA - timestampB
              });
            }
            
            return timestampA - timestampB; // Ascending order (earliest first)
          });
          setAllInfractions(sortedInfractions);
        } catch (error) {
          console.error('Failed to load infractions:', error);
        } finally {
          setLoadingInfractions(false);
        }
      } else {
        // Sort provided infractions chronologically (earliest first)
        const sortedInfractions = [...infractions].sort((a, b) => {
          // Extract timestamp from multiple possible sources for reliable sorting
          const getTimestamp = (infraction: any) => {
            // Try timestamp field first
            if (infraction.timestamp) {
              const ts = new Date(infraction.timestamp).getTime();
              if (!isNaN(ts)) return ts;
            }
            
            // Try date_created field
            if (infraction.date_created) {
              const ts = new Date(infraction.date_created).getTime();
              if (!isNaN(ts)) return ts;
            }
            
            // Extract from SK field as fallback (format: viol_1762058680051_xxx or 1762058704-screenshot_upload)
            if (infraction.sk) {
              const match = infraction.sk.match(/(\d{13})/); // Look for 13-digit timestamp
              if (match) {
                return parseInt(match[1], 10);
              }
            }
            
            // Fallback to 0 if no timestamp found
            console.warn('[INFRACTION_SORT] No valid timestamp found for infraction:', infraction.sk);
            return 0;
          };
          
          const timestampA = getTimestamp(a);
          const timestampB = getTimestamp(b);
          
          return timestampA - timestampB; // Ascending order (earliest first)
        });
        setAllInfractions(sortedInfractions);
      }
    };

    loadInfractions();
  }, [interviewId, infractions]);

  // Robust categorization logic - 2 categories only
  const categorizeInfraction = (infraction: Infraction) => {
    // INFRACTIONS: Actual violations requiring attention
    const isInfraction = () => {
      // Primary: Description contains "Violation:"
      if (infraction.description?.includes('Violation:')) return true;
      
      // Metadata flags indicating violations
      if (infraction.metadata?.is_routine_monitoring === 'false' && 
          infraction.metadata?.violation_id) return true;
          
      // Type indicates security violation
      if (['window_focus_lost', 'prohibited_app_detected', 'multiple_monitors_detected', 
           'display_change_detected', 'clipboard_access', 'network_activity',
           'security_event', 'window-focus', 'prohibited-app', 'multiple-monitors'].includes(infraction.type)) return true;
      
      // SK pattern for violations
      if (infraction.sk?.startsWith('viol_')) return true;
      
      return false;
    };

    // ROUTINE WEBCAM MONITORING: All webcam photos for monitoring
    const isRoutineWebcam = () => {
      // Type indicates monitoring
      if (['webcam_monitoring', 'screenshot_upload'].includes(infraction.type)) return true;
      
      // Webcam capture method (includes question milestones)
      if (infraction.metadata?.captureMethod === 'webcam') return true;
      
      // Routine monitoring metadata flags
      if (infraction.metadata?.is_routine_monitoring === 'true') return true;
      
      // General interview monitoring photos 
      if (infraction.metadata?.captureMethod === 'general_interview_monitoring') return true;
      
      // Capture reason indicates routine monitoring
      if (infraction.metadata?.captureReason &&
          ['interview_start', 'interview_end', 'time_interval'].includes(infraction.metadata.captureReason)) return true;
      
      // Question milestone photos
      
      // Description patterns for webcam photos (including "Security event webcam photo" which is actually routine)
      if (infraction.description?.includes('webcam photo') || 
          infraction.description?.includes('Security event webcam') ||
          infraction.description?.includes('General interview photo')) return true;
          
      return false;
    };

    if (isInfraction()) return 'infraction';
    if (isRoutineWebcam()) return 'routine_webcam';
    
    // Log uncategorized items for debugging
    console.warn('[INFRACTION_CATEGORIZATION] Uncategorized item:', {
      sk: infraction.sk,
      description: infraction.description,
      type: infraction.type,
      metadata: infraction.metadata
    });
    return 'uncategorized';
  };

  // Categorize infractions into groups
  const categorizedInfractions = React.useMemo(() => {
    const infractions: Infraction[] = [];
    const routineWebcam: Infraction[] = [];
    const uncategorized: Infraction[] = [];

    allInfractions.forEach(infraction => {
      const category = categorizeInfraction(infraction);
      switch (category) {
        case 'infraction':
          infractions.push(infraction);
          break;
        case 'routine_webcam':
          routineWebcam.push(infraction);
          break;
        default:
          uncategorized.push(infraction);
      }
    });

    // Sort each group by timestamp (newest first)
    infractions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    routineWebcam.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return { infractions, routineWebcam, uncategorized };
  }, [allInfractions]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };


  // Convert technical descriptions to human-readable format
  const getHumanReadableDescription = (description: string) => {
    // Map for common security event patterns
    const eventPatterns: Record<string, string> = {
      'Security event: window_focus_lost': 'Window Focus Lost',
      'Security event: window_blur': 'Window Minimized/Hidden',
      'Security event: multiple_monitors_detected': 'Multiple Monitors Detected',
      'Security event: display_change': 'Display Configuration Changed',
      'Security event: prohibited_app_detected': 'Prohibited Application Detected',
      'Security event: clipboard_copy': 'Clipboard Copy Activity',
      'Security event: clipboard_paste': 'Clipboard Paste Activity',
      'Security event: system_suspicious': 'Suspicious System Activity',
      'Violation: window_focus_lost - Application lost focus': 'Application Lost Focus',
      'Violation: window_blur - Window minimized': 'Window Minimized',
      'Violation: multiple_monitors_detected - Multiple displays': 'Multiple Displays Detected',
      'Violation: prohibited_app_detected - Unauthorized software': 'Unauthorized Software Detected',
    };

    // Check for exact matches first
    if (eventPatterns[description]) {
      return eventPatterns[description];
    }

    // Handle generic patterns
    if (description.startsWith('Security event: ')) {
      const eventType = description.replace('Security event: ', '');
      return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    if (description.startsWith('Violation: ') && description.includes(' - ')) {
      const parts = description.split(' - ');
      if (parts.length >= 2) {
        return parts[1]; // Return the human description part
      }
    }

    // Handle question screenshots
    if (description.includes('Question ') && description.includes('screenshot')) {
      const match = description.match(/Question (\d+)/);
      return match ? `Question ${match[1]} Screenshot` : 'Question Screenshot';
    }

    // Handle interview end photos FIRST (specific match)
    if (description.includes('interview_end')) {
      return 'Interview End Photo';
    }

    // Handle interview start photos (general match)
    if (description.includes('General interview photo') || description.includes('interview_start')) {
      return 'Interview Start Photo';
    }

    // Default fallback - clean up underscores and capitalize
    return description.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };


  const handlePreview = (infraction: Infraction) => {
    setSelectedInfraction(infraction);
    setPreviewOpen(true);
  };

  // Load preview URL for a specific infraction on-demand with caching
  const loadPreviewUrl = async (infraction: Infraction): Promise<string | null> => {
    console.log('loadPreviewUrl called for:', {
      sk: infraction.sk,
      evidence_r2_key: infraction.evidence_r2_key,
      evidence_type: infraction.evidence_type,
      description: infraction.description
    });

    if (!infraction.sk) {
      console.log('No infraction.sk found');
      return null;
    }
    
    // Check cache first to prevent duplicate API calls
    if (inlinePreviewUrls[infraction.sk]) {
      console.log('Using cached URL for', infraction.sk);
      return inlinePreviewUrls[infraction.sk];
    }

    try {
      console.log('Making API call for infraction:', infraction.sk);
      const response = await infractionsAPI.getInfractionEvidence(infraction.sk, interviewId);
      console.log('API response:', response);
      
      if (response.evidence_url) {
        // Cache the URL to prevent future API calls
        setInlinePreviewUrls(prev => ({
          ...prev,
          [infraction.sk]: response.evidence_url
        }));
        return response.evidence_url;
      } else {
        console.log('No evidence_url in response');
      }
    } catch (error) {
      console.error(`Failed to load preview for ${infraction.sk}:`, error);
    }
    
    return null;
  };

  const handleJumpToReplay = (timestamp: string) => {
    if (onJumpToReplay) {
      onJumpToReplay(new Date(timestamp).getTime());
      setPreviewOpen(false);
    }
  };

  const renderEvidencePreview = (infraction: Infraction) => {
    if (!infraction || !infraction.sk) return null;

    return <EvidencePreview infraction={infraction} onVideoStateChange={setVideoPlaying} />;
  };

  const EvidencePreview: React.FC<{ infraction: Infraction; onVideoStateChange: (playing: boolean) => void }> = ({ infraction, onVideoStateChange }) => {
    const [evidenceUrl, setEvidenceUrl] = useState<string | null>(inlinePreviewUrls[infraction.sk] || null);
    const [loadingEvidence, setLoadingEvidence] = useState(false);

    // Sync local state with cache updates
    useEffect(() => {
      if (inlinePreviewUrls[infraction.sk] && inlinePreviewUrls[infraction.sk] !== evidenceUrl) {
        console.log('EvidencePreview syncing cached URL for:', infraction.sk);
        setEvidenceUrl(inlinePreviewUrls[infraction.sk]);
      }
    }, [inlinePreviewUrls[infraction.sk], infraction.sk]);

    useEffect(() => {
      const loadEvidenceUrl = async () => {
        // Check both local state and cache before loading
        if (!infraction.sk || evidenceUrl || inlinePreviewUrls[infraction.sk]) return;
        
        setLoadingEvidence(true);
        try {
          // Use the cached loading function to prevent duplicate API calls
          const url = await loadPreviewUrl(infraction);
          setEvidenceUrl(url);
        } catch (error) {
          console.error('Failed to load evidence URL:', error);
        } finally {
          setLoadingEvidence(false);
        }
      };

      loadEvidenceUrl();
    }, [infraction.sk]); // Simplified dependency

    if (loadingEvidence) {
      return <LinearProgress />;
    }

    if (!evidenceUrl) {
      return <Alert severity="warning">Evidence not available</Alert>;
    }

    // Detect file type from URL instead of evidence_type field
    const isVideoFile = evidenceUrl?.includes('.webm') || evidenceUrl?.includes('.mp4');
    const isImageFile = evidenceUrl?.includes('.png') || evidenceUrl?.includes('.jpg') || evidenceUrl?.includes('.jpeg');

    if (isImageFile) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <CachedEvidence
            src={evidenceUrl}
            alt="Infraction Screenshot"
            width="100%"
            height="auto"
            objectFit="contain"
            borderRadius={0}
            autoCache={true}
            style={{
              maxWidth: '100%',
              maxHeight: '500px',
              border: 'none'
            }}
            onError={(e) => {
              console.warn('Modal evidence failed to load:', evidenceUrl);
            }}
          />
        </Box>
      );
    } else if (isVideoFile) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '500px'
            }}
            onPlay={() => onVideoStateChange(true)}
            onPause={() => onVideoStateChange(false)}
            onError={(e) => {
              console.error('Video loading error for:', evidenceUrl, e);
              console.error('Video element:', e.target);
              console.error('Error type:', e.type);
            }}
            onLoadStart={() => console.log('Video load started:', evidenceUrl)}
            onCanPlay={() => console.log('Video can play:', evidenceUrl)}
            onLoadedData={() => console.log('Video data loaded:', evidenceUrl)}
          >
            <source src={evidenceUrl} type="video/webm" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    return null;
  };

  // Inline Preview Component with on-demand loading
  const InlinePreview: React.FC<{ infraction: Infraction }> = ({ infraction }) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(inlinePreviewUrls[infraction.sk] || null);
    const [isLoading, setIsLoading] = useState(false);
    const [videoError, setVideoError] = useState(false);

    // Sync local state with cache updates
    useEffect(() => {
      if (inlinePreviewUrls[infraction.sk] && inlinePreviewUrls[infraction.sk] !== previewUrl) {
        console.log('InlinePreview syncing cached URL for:', infraction.sk);
        setPreviewUrl(inlinePreviewUrls[infraction.sk]);
      }
    }, [inlinePreviewUrls[infraction.sk], infraction.sk]);

    // Load preview URL on mount if not already cached
    useEffect(() => {
      const loadUrl = async () => {
        // Check both local state and cache before loading
        if (!previewUrl && !inlinePreviewUrls[infraction.sk] && infraction.sk && !isLoading) {
          console.log('InlinePreview loading URL for:', infraction.sk);
          setIsLoading(true);
          const url = await loadPreviewUrl(infraction);
          console.log('InlinePreview got URL:', url);
          setPreviewUrl(url);
          setIsLoading(false);
        }
      };

      loadUrl();
    }, [infraction.sk]); // Only depend on infraction.sk to avoid loops
    
    if (isLoading) {
      return (
        <Box sx={{ 
          width: 80, 
          height: 60, 
          backgroundColor: 'grey.100', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderRadius: 1
        }}>
          <CircularProgress size={20} />
        </Box>
      );
    }

    if (!previewUrl) {
      return (
        <Box sx={{ 
          width: 80, 
          height: 60, 
          backgroundColor: 'grey.200', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderRadius: 1
        }}>
          <Typography variant="caption" color="text.secondary">No Preview</Typography>
        </Box>
      );
    }

    // Detect file type from URL instead of evidence_type field
    const isVideoFile = previewUrl?.includes('.webm') || previewUrl?.includes('.mp4');
    const isImageFile = previewUrl?.includes('.png') || previewUrl?.includes('.jpg') || previewUrl?.includes('.jpeg');

    if (isImageFile) {
      return (
        <Box 
          sx={{
            cursor: 'pointer',
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 200ms ease'
            }
          }}
          onClick={() => handlePreview(infraction)}
        >
          <CachedEvidence
            src={previewUrl}
            alt="Infraction Thumbnail"
            width={80}
            height={60}
            objectFit="cover"
            borderRadius={4}
            autoCache={true}  // Enable caching for cost reduction
            forceFallback={false}  // Will auto-fallback if caching fails
            style={{ 
              border: 'none'
            }}
            onError={() => {
              console.warn('Thumbnail failed to load:', previewUrl);
            }}
          />
        </Box>
      );
    } else if (isVideoFile) {
      // If video failed to load, show simple play button
      if (videoError) {
        return (
          <Box sx={{ 
            width: 80, 
            height: 60, 
            backgroundColor: 'grey.900', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'grey.800',
              transition: 'background-color 200ms ease'
            }
          }}
          onClick={() => handlePreview(infraction)}
          >
            <PlayArrow sx={{ color: 'white', fontSize: 32 }} />
          </Box>
        );
      }
      
      return (
        <Box
          sx={{ 
            width: 80, 
            height: 60, 
            borderRadius: 1,
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 200ms ease'
            }
          }}
          onClick={() => handlePreview(infraction)}
        >
          <video
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            muted
            playsInline
            onError={(e) => {
              console.error('Inline video preview error for:', previewUrl, e);
              setVideoError(true);
              // Test the URL directly to check HTTP headers
              fetch(previewUrl, { method: 'HEAD' })
                .then(response => {
                  console.error('WebM HTTP status:', response.status);
                  console.error('WebM Content-Type:', response.headers.get('Content-Type'));
                  console.error('WebM Content-Length:', response.headers.get('Content-Length'));
                  console.error('WebM Cache-Control:', response.headers.get('Cache-Control'));
                })
                .catch(fetchError => {
                  console.error('WebM fetch test failed:', fetchError);
                });
            }}
            onLoadStart={() => console.log('Inline video load started:', previewUrl)}
            onCanPlay={() => console.log('Inline video can play:', previewUrl)}
          >
            <source src={previewUrl} type="video/webm" />
          </video>
          {/* Overlay play button */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              color: 'white'
            }}
          >
            <PlayArrow sx={{ fontSize: 24 }} />
          </Box>
        </Box>
      );
    }

    return null;
  };

  const renderInfractionCard = (infraction: Infraction, index: number, isRoutineMonitoring: boolean = false) => (
    <Card key={`${infraction.sk}-${index}`} sx={{ 
      mb: 1.5, // Comfortable spacing
      // Conservative background colors
      backgroundColor: isRoutineMonitoring 
        ? 'rgba(33, 150, 243, 0.03)' 
        : 'rgba(244, 67, 54, 0.03)',
      border: isRoutineMonitoring 
        ? '1px solid rgba(33, 150, 243, 0.3)' 
        : '1px solid rgba(244, 67, 54, 0.3)',
      borderRadius: 1,
      // Subtle shadow
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      // Staggered entrance animation (keep this)
      animation: `${slideInStaggered} ${0.6 + index * 0.1}s ease-out forwards`,
      opacity: 0, // Start invisible for animation
      // Performance optimizations
      contain: 'layout style paint',
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      // Conservative hover effects
      '&:hover': {
        backgroundColor: isRoutineMonitoring 
          ? 'rgba(33, 150, 243, 0.05)' 
          : 'rgba(244, 67, 54, 0.05)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        transition: 'all 200ms ease',
      },
      // Focus state for accessibility
      '&:focus-within': {
        outline: `2px solid ${isRoutineMonitoring ? 'rgba(33, 150, 243, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`,
        outlineOffset: '2px',
      }
    }}>
      <CardHeader
        avatar={<InlinePreview infraction={infraction} />}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2">
              {formatTimestamp(infraction.timestamp)}
            </Typography>
            {!isRoutineMonitoring && (
              <Chip
                icon={<Warning />}
                label="VIOLATION"
                color="error"
                size="small"
                variant="filled"
                sx={{ 
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(244, 67, 54, 0.2)'
                }}
              />
            )}
            {isRoutineMonitoring && (
              <Chip
                icon={<PhotoCamera />}
                label="MONITORING"
                color="primary"
                size="small"
                variant="filled"
                sx={{ 
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(33, 150, 243, 0.2)'
                }}
              />
            )}
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {getHumanReadableDescription(infraction.description)}
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        
        {/* Candidate Explanation */}
        {infraction.explanation && (
          <Paper 
            sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderLeft: '4px solid', 
              borderLeftColor: 'primary.main' 
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <FormatQuote sx={{ color: 'primary.main', fontSize: '1rem', mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="primary.dark" fontWeight="bold">
                  Candidate Explanation:
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                  {infraction.explanation}
                </Typography>
                {infraction.explanation_date && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Submitted: {formatTimestamp(infraction.explanation_date)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </CardContent>
      <CardActions>
        {infraction.evidence_r2_key && (
          <Tooltip title="View Full Evidence">
            <IconButton onClick={() => handlePreview(infraction)} size="small">
              <Visibility />
            </IconButton>
          </Tooltip>
        )}
        {onJumpToReplay && !isRoutineMonitoring && (
          <Tooltip title="Jump to Replay">
            <Button 
              size="small" 
              onClick={() => handleJumpToReplay(infraction.timestamp)}
              startIcon={seekingTimestamp === new Date(infraction.timestamp).getTime() ? 
                <CircularProgress size={16} color="inherit" /> : 
                <PlayArrow />
              }
              disabled={seekingTimestamp !== null}
            >
              {seekingTimestamp === new Date(infraction.timestamp).getTime() ? 'Seeking...' : 'Jump to Replay'}
            </Button>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );

  if (loadingInfractions) {
    return (
      <Box>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading infractions...</Typography>
      </Box>
    );
  }

  if (allInfractions.length === 0) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        No security infractions detected during this interview.
      </Alert>
    );
  }

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Infractions Accordion */}
      {categorizedInfractions.infractions.length > 0 && (
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: 'rgba(244, 67, 54, 0.05)', 
              borderLeft: '4px solid #f44336',
              '&:hover': { 
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                transform: 'translateY(-1px)',
                transition: 'all 200ms ease'
              }
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Warning sx={{ color: 'error.main' }} />
              <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                Infractions
              </Typography>
              <Chip 
                label={`${categorizedInfractions.infractions.length} violation${categorizedInfractions.infractions.length > 1 ? 's' : ''}`} 
                size="small" 
                color="error"
                variant="filled"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: 'rgba(244, 67, 54, 0.02)' }}>
            {categorizedInfractions.infractions.map((infraction, index) => 
              renderInfractionCard(infraction, index)
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Routine Webcam Monitoring Accordion */}
      {categorizedInfractions.routineWebcam.length > 0 && (
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: 'rgba(33, 150, 243, 0.05)', 
              borderLeft: '4px solid #2196f3',
              '&:hover': { 
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                transform: 'translateY(-1px)',
                transition: 'all 200ms ease'
              }
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <PhotoCamera sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Routine Webcam Monitoring
              </Typography>
              <Chip 
                label={`${categorizedInfractions.routineWebcam.length} photo${categorizedInfractions.routineWebcam.length > 1 ? 's' : ''}`} 
                size="small" 
                color="primary"
                variant="filled"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: 'rgba(33, 150, 243, 0.02)' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Automated webcam photos captured during the interview for security monitoring.
            </Typography>
            {categorizedInfractions.routineWebcam.map((infraction, index) => 
              renderInfractionCard(infraction, index, true)
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Uncategorized Items (for debugging) */}
      {categorizedInfractions.uncategorized.length > 0 && (
        <Accordion sx={{ mb: 2, border: '1px solid orange' }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning sx={{ color: 'orange' }} />
              <Typography variant="h6" sx={{ color: 'orange' }}>
                Uncategorized Items ({categorizedInfractions.uncategorized.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="warning" sx={{ mb: 2 }}>
              These items could not be automatically categorized. This may indicate a data format issue.
            </Alert>
            {categorizedInfractions.uncategorized.map((infraction, index) => 
              renderInfractionCard(infraction, index)
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Evidence Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {isRoutineMonitoring ? 'Routine Webcam Monitoring Evidence' : 'Infraction Evidence'}
            </Typography>
            {selectedInfraction && !isRoutineMonitoring && (
              <Chip
                icon={<Warning />}
                label="VIOLATION"
                color="error"
                size="small"
                variant="filled"
              />
            )}
            {selectedInfraction && isRoutineMonitoring && (
              <Chip
                icon={<PhotoCamera />}
                label="MONITORING"
                color="primary"
                size="small"
                variant="filled"
              />
            )}
          </Box>
          {selectedInfraction && (
            <Typography variant="body2" color="text.secondary">
              {formatTimestamp(selectedInfraction.timestamp)} | {getHumanReadableDescription(selectedInfraction.description)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedInfraction && renderEvidencePreview(selectedInfraction)}
        </DialogContent>
        <DialogActions>
          {selectedInfraction && onJumpToReplay && categorizeInfraction(selectedInfraction) === 'infraction' && (
            <Button 
              onClick={() => handleJumpToReplay(selectedInfraction.timestamp)}
              startIcon={seekingTimestamp === new Date(selectedInfraction.timestamp).getTime() ? 
                <CircularProgress size={16} color="inherit" /> : 
                <PlayArrow />
              }
              disabled={seekingTimestamp !== null}
            >
              {seekingTimestamp === new Date(selectedInfraction.timestamp).getTime() ? 'Seeking...' : 'Jump to Replay'}
            </Button>
          )}
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InfractionComponent;